import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flame, Pencil, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { myBoostsQuery } from "@/lib/queries";
import { formatHeat, formatMoney, labelForType } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";
import { analytics, takePendingCheckout } from "@/lib/analytics";
import { useT } from "@/i18n";
import { MyPageLink } from "@/components/my-page-link";
import { proxiedImage } from "@/lib/remote-image";

export const Route = createFileRoute("/app/")({
  validateSearch: (search: Record<string, unknown>): { checkout?: string | undefined } => ({
    checkout: typeof search["checkout"] === "string" ? search["checkout"] : undefined,
  }),
  head: () =>
    privatePageMetadata(
      "/app",
      "Dashboard | BeHot.Now",
      "Your BeHot.Now dashboard.",
    ),
  component: Overview,
});

function Overview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkout } = Route.useSearch();
  const { t } = useT();

  useEffect(() => {
    if (checkout === "success") {
      const pending = takePendingCheckout();
      analytics.purchase({
        transactionId: `${Date.now()}`,
        kind: pending?.kind ?? "boost",
        valueUsd: pending?.valueUsd ?? 0,
      });
      toast.success(t("dashboard.overview.toast.paymentReceived"));
      void navigate({ to: "/app", search: {}, replace: true });
    }
  }, [checkout, navigate]);

  const { data: listings = [] } = useQuery({
    queryKey: ["my-listings", user?.id],
    enabled: Boolean(user),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("id,name,slug,listing_type,moderation_status,headline,logo_url")
        .eq("owner_user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: wallet } = useQuery({
    queryKey: ["my-wallet", user?.id],
    enabled: Boolean(user),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wallets")
        .select("topup_balance_cents,earn_balance_cents,referral_balance_cents")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: boosts = [] } = useQuery(myBoostsQuery(user?.id));
  const activeBoosts = boosts.filter((b) => b.status === "active");
  const activeHeat = activeBoosts.reduce((s, b) => s + Number(b.value_cents), 0);

  return (
    <div className="space-y-8">
      <MyPageLink />

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="card-surface p-5">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            <Wallet className="size-3.5" /> {t("dashboard.overview.heatWallet")}
          </p>
          <p className="mt-1 font-display text-2xl font-bold">
            {formatHeat(Number(wallet?.topup_balance_cents ?? 0))} Heat
          </p>
          <p className="text-xs text-muted-foreground">{t("dashboard.overview.heatWalletDesc")}</p>
          <Button asChild variant="hot" size="sm" className="mt-3">
            <Link to="/app/wallets">{t("dashboard.overview.topUpHeat")}</Link>
          </Button>

        </div>
        <div className="card-surface p-5">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            <Flame className="size-3.5" /> {t("dashboard.overview.referralHeat")}
          </p>
          <p className="mt-1 font-display text-2xl font-bold">
            {formatHeat(Number(wallet?.referral_balance_cents ?? 0))} Heat
          </p>
          <p className="text-xs text-muted-foreground">{t("dashboard.overview.referralHeatDesc")}</p>
          <Button asChild variant="outline" size="sm" className="mt-3">
            <Link to="/app/referrals">{t("dashboard.overview.inviteEarn")}</Link>
          </Button>
        </div>
        <div className="card-surface p-5">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("dashboard.overview.earnWallet")}
          </p>
          <p className="mt-1 font-display text-2xl font-bold">
            {formatMoney(Number(wallet?.earn_balance_cents ?? 0))}
          </p>
          <p className="text-xs text-muted-foreground">{t("dashboard.overview.earnWalletDesc")}</p>
          <Button asChild variant="outline" size="sm" className="mt-3">
            <Link to="/app/wallets">{t("dashboard.overview.withdraw")}</Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="card-surface p-5">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            <Flame className="size-3.5" /> {t("dashboard.overview.activeHeat")}
          </p>
          <p className="mt-1 font-display text-2xl font-bold">{formatHeat(activeHeat)}</p>
        </div>
        <div className="card-surface p-5">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("dashboard.overview.activeBoosts")}
          </p>
          <p className="mt-1 font-display text-2xl font-bold">{activeBoosts.length}</p>
        </div>
      </div>

      <section>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">{t("dashboard.overview.yourListings")}</h2>
          <Button asChild variant="hot" size="sm">
            <Link to="/get-hot" search={{ listing: undefined, category: undefined }}>
              {t("dashboard.overview.newListing")}
            </Link>
          </Button>
        </div>

        <div className="mt-4 space-y-3">
          {listings.length === 0 && (
            <div className="card-surface p-6 text-sm text-muted-foreground">
              {t("dashboard.overview.noListings")}
            </div>
          )}
          {listings.map((l) => (
            <div key={l.id} className="card-surface flex flex-wrap items-center gap-3 p-4">
              {l.logo_url ? (
                <img src={proxiedImage(l.logo_url)} alt="" className="size-10 rounded-xl object-cover" />
              ) : (
                <div className="flex size-10 items-center justify-center rounded-xl bg-accent font-bold text-accent-foreground">
                  {l.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{l.name}</p>
                <p className="truncate text-sm text-muted-foreground">
                  {labelForType(l.listing_type)}
                </p>
              </div>
              <Badge variant={l.moderation_status === "approved" ? "secondary" : "outline"}>
                {l.moderation_status}
              </Badge>
              <Button asChild variant="outline" size="sm">
                <Link to="/app/listings/$id" params={{ id: l.id }}>
                  <Pencil className="size-4" /> {t("dashboard.overview.edit")}
                </Link>
              </Button>
              <Button asChild variant="hot" size="sm">
                <Link to="/get-hot" search={{ listing: l.slug, category: undefined }}>
                  {t("dashboard.overview.boost")}
                </Link>
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-bold">{t("dashboard.overview.activeBoostsHeading")}</h2>
        <div className="mt-4 space-y-3">
          {activeBoosts.length === 0 && (
            <div className="card-surface p-6 text-sm text-muted-foreground">
              {t("dashboard.overview.noActiveHeat")}
            </div>
          )}
          {activeBoosts.map((b) => (
            <div key={b.id} className="card-surface flex items-center gap-3 p-4 text-sm">
              <Flame className="size-4 text-primary" />
              <span className="font-semibold">{formatHeat(b.value_cents)} Heat</span>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">
                {t("dashboard.overview.onCategory", { listing: b.listing?.name ?? "", category: b.category?.name ?? "" })}
              </span>
              <span className="text-xs text-muted-foreground">
                {t("dashboard.overview.cools", { date: new Date(b.expires_at).toLocaleDateString() })}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
